import type { VendorSummary, PARInvoiceRow } from "@/lib/par/csvInvoiceParser";
import type { NegotiationThread, VendorTerms } from "@/types/negotiations";
import type {
  SuggestedVendor,
  Signal,
  NegotiationSignalConfig,
} from "@/lib/par/negotiationSuggestions";
import {
  computeVendorSignalsAndScore,
  computeTopQuartileThreshold,
  DEFAULT_NEGOTIATION_CONFIG,
} from "@/lib/par/negotiationSuggestions";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RosterStatus =
  | "needs_attention"
  | "in_conversation"
  | "agreed"
  | "excluded"
  | "suggested"
  | "none";

/**
 * Prep-pipeline stage.
 * Engaged  = has an active/needs_attention thread (outreach sent)
 * Ready    = has a contact (email) and is not excluded — ready to engage
 * Qualified = has a vendor_contacts profile (contact set) but no thread yet
 * Identify = suggested but no contact on file
 */
export type RosterStage = "Identify" | "Qualified" | "Ready" | "Engaged";

export interface VendorContact {
  vendor_name: string;
  supplier_email: string | null;
  contact_name: string | null;
  negotiation_mode: "draft_wait" | "auto";
  excluded: boolean;
  exclusion_reason?: string | null;
  exclusion_category?: string | null;
  tier?: "strategic" | "standard" | "transactional" | null;
}

export interface RosterEntry {
  vendor_name: string;
  status: RosterStatus;
  stage: RosterStage;
  /** Contact from vendor_contacts (if any). */
  contact: VendorContact | null;
  /** Negotiation mode from contact (or default). */
  mode: "draft_wait" | "auto";
  /** Vendor tier from contact. */
  tier: "strategic" | "standard" | "transactional" | null;
  /** The most relevant thread for this vendor (needs_attention / active). */
  thread: NegotiationThread | null;
  /** Agreed vendor terms (if any). */
  vendorTerms: VendorTerms | null;
  /** Whether this vendor appears in suggestions. */
  isSuggested: boolean;
  total_invoiced: number;
  /** Typical/most-common payment terms string from this vendor's invoices. */
  payment_terms: string;
  /** Average days paid early (positive = early, negative = late). */
  avg_days_paid: number;
  /** Negotiation signals computed for this vendor (all vendors, not just excluded set). */
  signals: Signal[];
  /** Composite score based on active signals and weights. */
  score: number;
  /** True if this vendor has a needs_attention thread (surfaced as an indicator). */
  needs_attention: boolean;
  /** True if this vendor has been explicitly excluded from negotiations. */
  is_excluded: boolean;
  /** The reason the vendor was excluded (from vendor_contacts.exclusion_reason), if any. */
  exclusion_reason: string | null;
  /** All distinct payment-term strings this vendor has had across invoice rows. */
  terms_used: string[];
  /**
   * IDs of campaigns this vendor currently belongs to (via a thread with a campaign_id).
   * Empty array if the vendor is not in any campaign.
   */
  campaignIds: string[];
  /**
   * Campaign names corresponding to campaignIds (same order).
   */
  campaignNames: string[];
}

/**
 * Time window for spend computation. "all" = no date bounding (current behaviour).
 * "3m" / "6m" / "12m" = last N calendar months relative to today.
 */
export type SpendWindow = "3m" | "6m" | "12m" | "all";

export interface BuildVendorRosterParams {
  vendors: VendorSummary[];
  threads: NegotiationThread[];
  vendorTerms: VendorTerms[];
  contacts: VendorContact[];
  suggestions: SuggestedVendor[];
  /** All PAR invoice rows — used to compute per-vendor signals. */
  allInvoiceRows?: PARInvoiceRow[];
  /** Signal config — defaults to DEFAULT_NEGOTIATION_CONFIG. */
  signalConfig?: NegotiationSignalConfig;
  /**
   * Time window for spend computation. Defaults to "12m" (last 12 months).
   * When rows carry `invoiced_date`, only rows within the window contribute to
   * `total_invoiced`. Falls back to the VendorSummary all-time total when no
   * rows are available.
   */
  spendWindow?: SpendWindow;
  /**
   * All campaigns — used to derive campaignIds / campaignNames on each RosterEntry.
   * If omitted, campaign membership fields will be empty.
   */
  campaigns?: import("@/types/negotiations").NegotiationCampaign[];
}

// ---------------------------------------------------------------------------
// Spend-window helpers
// ---------------------------------------------------------------------------

/**
 * Return the ISO date string representing the start of the window (inclusive),
 * or null for "all time".
 */
export function spendWindowCutoff(window: SpendWindow, now: Date = new Date()): Date | null {
  if (window === "all") return null;
  const months = window === "3m" ? 3 : window === "6m" ? 6 : 12;
  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() - months);
  return cutoff;
}

/**
 * Compute the total invoiced amount for a set of rows, optionally bounded by a
 * cutoff date (rows with `invoiced_date` < cutoff are excluded).
 * When `invoiced_date` is missing or unparseable, the row is included in "all"
 * mode and excluded from windowed mode (safe degradation).
 */
export function computeWindowedSpend(rows: PARInvoiceRow[], cutoff: Date | null): number {
  if (rows.length === 0) return 0;
  return rows.reduce((sum, row) => {
    if (cutoff !== null) {
      if (!row.invoiced_date) return sum; // no date → exclude from windowed calc
      const d = new Date(row.invoiced_date);
      if (isNaN(d.getTime()) || d < cutoff) return sum;
    }
    return sum + (row.amount_invoiced ?? 0);
  }, 0);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Thread statuses that represent an active/needs_attention engagement.
 * We treat "needs_attention" as the highest-priority active status.
 */
function isNeedsAttention(thread: NegotiationThread): boolean {
  return (thread.status as string) === "needs_attention";
}

function isInConversation(thread: NegotiationThread): boolean {
  return thread.status === "active";
}

function isAgreed(thread: NegotiationThread): boolean {
  return thread.status === "agreed";
}

/**
 * Given a list of threads for a vendor, find the highest-priority one.
 * Priority: needs_attention > active > agreed > other
 */
function bestThread(threads: NegotiationThread[]): NegotiationThread | null {
  if (threads.length === 0) return null;
  const order = ["needs_attention", "active", "agreed", "declined", "abandoned"];
  return (
    threads.slice().sort((a, b) => {
      const ai = order.indexOf(a.status as string);
      const bi = order.indexOf(b.status as string);
      return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    })[0] ?? null
  );
}

/**
 * Derive the roster stage for a vendor entry.
 *
 * Engaged  → has a needs_attention or active thread
 * Ready    → has a contact with a supplier email (can start engagement)
 * Qualified → has a contact profile but no email yet
 * Identify → suggested or known but no contact
 */
function deriveStage(
  contact: VendorContact | null,
  thread: NegotiationThread | null,
  isSuggested: boolean
): RosterStage {
  if (thread && (isNeedsAttention(thread) || isInConversation(thread) || isAgreed(thread))) {
    return "Engaged";
  }
  if (contact && contact.supplier_email) {
    return "Ready";
  }
  if (contact) {
    return "Qualified";
  }
  if (isSuggested) {
    return "Identify";
  }
  return "Identify";
}

// ---------------------------------------------------------------------------
// Core function
// ---------------------------------------------------------------------------

/**
 * Build a unified roster of all known vendors, each with a computed status
 * and stage for display in the Vendor Roster UI.
 *
 * Status precedence (highest → lowest):
 *   needs_attention > in_conversation > agreed > excluded > suggested > none
 */
export function buildVendorRoster({
  vendors,
  threads,
  vendorTerms,
  contacts,
  suggestions,
  allInvoiceRows = [],
  signalConfig,
  spendWindow = "12m",
  campaigns = [],
}: BuildVendorRosterParams): RosterEntry[] {
  const cfg = signalConfig ?? DEFAULT_NEGOTIATION_CONFIG;

  // Pre-compute the cutoff date for the selected spend window
  const spendCutoff = spendWindowCutoff(spendWindow);

  // Pre-compute top-quartile threshold once for signal computation
  const topQuartileThreshold = computeTopQuartileThreshold(vendors, cfg.high_spend_quantile);

  // Group invoice rows by vendor name
  const rowsByVendor = new Map<string, PARInvoiceRow[]>();
  for (const row of allInvoiceRows) {
    const key = row.vendor_name;
    if (!rowsByVendor.has(key)) rowsByVendor.set(key, []);
    rowsByVendor.get(key)!.push(row);
  }

  // Index contacts by vendor_name (lowercase for robustness)
  const contactByVendor = new Map<string, VendorContact>();
  for (const c of contacts) {
    contactByVendor.set(c.vendor_name.toLowerCase(), c);
  }

  // Index threads by vendor_name (the denormalized column)
  const threadsByVendor = new Map<string, NegotiationThread[]>();
  for (const t of threads) {
    const key = (t.vendor_name ?? "").toLowerCase();
    if (!key) continue;
    if (!threadsByVendor.has(key)) threadsByVendor.set(key, []);
    threadsByVendor.get(key)!.push(t);
  }

  // Index vendor_terms by supplier_email or supplier_name → vendor_name mapping
  // Since VendorTerms is keyed by supplier_email, we also check supplier_name
  const termsByVendor = new Map<string, VendorTerms>();
  for (const vt of vendorTerms) {
    if (vt.supplier_name) {
      termsByVendor.set(vt.supplier_name.toLowerCase(), vt);
    }
  }

  // Index suggestions by vendor_name
  const suggestionSet = new Set<string>(suggestions.map((s) => s.vendor_name.toLowerCase()));

  // Index campaigns by id → name
  const campaignById = new Map<string, string>();
  for (const c of campaigns) {
    campaignById.set(c.id, c.name);
  }

  // For each vendor (lowercase key), collect unique campaign ids from their threads
  const campaignIdsByVendor = new Map<string, string[]>();
  for (const t of threads) {
    if (!t.campaign_id || !t.vendor_name) continue;
    const key = t.vendor_name.toLowerCase();
    if (!campaignIdsByVendor.has(key)) campaignIdsByVendor.set(key, []);
    const existing = campaignIdsByVendor.get(key)!;
    if (!existing.includes(t.campaign_id)) existing.push(t.campaign_id);
  }

  // Collect all unique vendor names from all sources
  const allVendorNames = new Set<string>();
  for (const v of vendors) allVendorNames.add(v.vendor_name);
  for (const c of contacts) allVendorNames.add(c.vendor_name);
  for (const s of suggestions) allVendorNames.add(s.vendor_name);
  // Also include threads whose vendor_name may not be in the PAR data
  for (const t of threads) {
    if (t.vendor_name) allVendorNames.add(t.vendor_name);
  }

  const vendorByName = new Map<string, VendorSummary>();
  for (const v of vendors) vendorByName.set(v.vendor_name, v);

  /**
   * Derive the most representative payment terms string for a vendor.
   * Prefers the term that appears most often across invoice rows;
   * falls back to the first entry in VendorSummary.terms_used.
   */
  function derivePaymentTerms(vendorName: string, summary: VendorSummary | undefined): string {
    const rows = rowsByVendor.get(vendorName) ?? [];
    if (rows.length > 0) {
      const freq = new Map<string, number>();
      for (const r of rows) {
        if (r.terms) freq.set(r.terms, (freq.get(r.terms) ?? 0) + 1);
      }
      if (freq.size > 0) {
        return [...freq.entries()].sort((a, b) => b[1] - a[1])[0][0];
      }
    }
    return summary?.terms_used[0] ?? "";
  }

  const roster: RosterEntry[] = [];

  for (const name of allVendorNames) {
    const key = name.toLowerCase();
    const contact = contactByVendor.get(key) ?? null;
    const vendorThreads = threadsByVendor.get(key) ?? [];
    const thread = bestThread(vendorThreads);
    const terms = termsByVendor.get(key) ?? null;
    const isSuggested = suggestionSet.has(key);
    const summary = vendorByName.get(name);

    // Determine status using precedence
    let status: RosterStatus = "none";

    if (vendorThreads.some(isNeedsAttention)) {
      status = "needs_attention";
    } else if (vendorThreads.some(isInConversation)) {
      status = "in_conversation";
    } else if (terms != null || vendorThreads.some(isAgreed)) {
      status = "agreed";
    } else if (contact?.excluded) {
      status = "excluded";
    } else if (isSuggested) {
      status = "suggested";
    } else {
      status = "none";
    }

    const stage = deriveStage(contact, thread, isSuggested);
    const mode: "draft_wait" | "auto" = contact?.negotiation_mode ?? "draft_wait";
    const tier = contact?.tier ?? null;

    // Compute signals + score for this vendor (no exclusion filter — all vendors)
    const vendorRows = rowsByVendor.get(name) ?? [];
    const { signals, score } = summary
      ? computeVendorSignalsAndScore(summary, vendorRows, topQuartileThreshold, cfg)
      : { signals: [], score: 0 };

    const paymentTerms = derivePaymentTerms(name, summary);
    const avgDaysPaid = summary?.avg_days_paid_early ?? 0;
    const needsAttention = vendorThreads.some(isNeedsAttention);
    // Collect all distinct terms across invoice rows; fall back to summary.terms_used
    const termsUsed: string[] = (() => {
      const rows = rowsByVendor.get(name) ?? [];
      if (rows.length > 0) {
        const set = new Set<string>();
        for (const r of rows) {
          if (r.terms) set.add(r.terms);
        }
        if (set.size > 0) return [...set].sort();
      }
      return summary?.terms_used ?? [];
    })();

    const vendorCampaignIds = campaignIdsByVendor.get(key) ?? [];
    const vendorCampaignNames = vendorCampaignIds.map((cid) => campaignById.get(cid) ?? cid);

    roster.push({
      vendor_name: name,
      status,
      stage,
      contact,
      mode,
      tier,
      thread,
      vendorTerms: terms,
      isSuggested,
      // Use windowed spend when invoice rows are available; fall back to
      // VendorSummary all-time total when no rows are present.
      total_invoiced:
        vendorRows.length > 0
          ? computeWindowedSpend(vendorRows, spendCutoff)
          : (summary?.total_invoiced ?? 0),
      payment_terms: paymentTerms,
      avg_days_paid: avgDaysPaid,
      signals,
      score,
      needs_attention: needsAttention,
      is_excluded: contact?.excluded ?? false,
      exclusion_reason: contact?.exclusion_reason ?? null,
      terms_used: termsUsed,
      campaignIds: vendorCampaignIds,
      campaignNames: vendorCampaignNames,
    });
  }

  // Sort: by status priority, then by total_invoiced descending
  const statusOrder: RosterStatus[] = [
    "needs_attention",
    "in_conversation",
    "agreed",
    "excluded",
    "suggested",
    "none",
  ];
  roster.sort((a, b) => {
    const ai = statusOrder.indexOf(a.status);
    const bi = statusOrder.indexOf(b.status);
    if (ai !== bi) return ai - bi;
    return b.total_invoiced - a.total_invoiced;
  });

  return roster;
}
